import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  Spinner,
  // Modal,
} from "react-bootstrap";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";
import AsyncSelect from "react-select/async";
import { useATV } from "../context/ATVContext";
import { useWarehouse } from "../context/WarehouseContext";
import { useCategory } from "../context/CategoryContext";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { formatPrice } from "utils/utils";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconFilter,
  IconFilterOff,
  IconRefresh,
} from "@tabler/icons-react";
import WorkshopTopbar from "./WorkshopTopbar";

const ServiceReport = () => {
  const { atvs } = useATV();
  const { warehouses } = useWarehouse();
  const { categories } = useCategory();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    return {
      start_date: today,
      end_date: today,
      atv_id: "",
      inventory_id: "",
      category_id: "",
      warehouse_id: "",
      search: "",
      limit: 20,
      page: 1,
      sort: "desc",
      order_by: "service_date",
    };
  });

  const [showFilters, setShowFilters] = useState(false);

  // Load report data
  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = { ...filters };
      // Remove empty filters
      Object.keys(queryParams).forEach((key) => {
        if (!queryParams[key]) delete queryParams[key];
      });

      const queryString = buildQueryString(queryParams);
      const response = await getRequest(
        `/service-reports/services${queryString}`
      );

      if (response.success && response.data) {
        setReportData({
          ...response.data,
          pagination: response.pagination || null,
        });
      } else {
        console.error("Failed to load report data:", response.message);
      }
    } catch (error) {
      console.error("Error loading report data:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load inventory options for filter
  const loadInventoryOptions = async (inputValue) => {
    try {
      const queryParams = {
        search: inputValue || "",
        include: "category",
        limit: 100,
      };
      const queryString = buildQueryString(queryParams);
      const response = await getRequest(`/inventory${queryString}`);

      if (response.success && response.data) {
        return response.data.map((item) => ({
          value: item.id,
          label: `${item.name} (${item.unit}) - ${
            item.category?.name || "No Category"
          }`,
        }));
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error loading inventory options:", error);
      return [];
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field !== "page" ? { page: 1 } : {}),
    }));
  };

  // Reset filters
  const resetFilters = () => {
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    setFilters({
      start_date: today,
      end_date: today,
      atv_id: "",
      inventory_id: "",
      category_id: "",
      warehouse_id: "",
      search: "",
      limit: 20,
      page: 1,
      sort: "desc",
      order_by: "service_date",
    });
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  // const formatCurrency = (amount) => {
  //   return new Intl.NumberFormat("id-ID", {
  //     style: "currency",
  //     currency: "IDR",
  //   }).format(amount);
  // };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID");
  };

  const getServiceItemsText = (service) => {
    const inventoryItems = service.inventory_usage?.items || [];
    const manualItems = service.manual_items || [];
    const formattedInventory = inventoryItems.map(
      (item) => `${item.inventory_name || item.name || "-"} (${item.quantity || 0} ${item.unit || ""})`
    );
    const formattedManual = manualItems.map(
      (item) => `${item.item_name || "-"} (${item.quantity || 0} ${item.unit || ""})`
    );
    return [...formattedInventory, ...formattedManual].join(", ") || "-";
  };

  const buildExportRows = (services) =>
    services.map((service, index) => ({
      No: index + 1,
      "Service Date": formatDate(service.service_date),
      "Next Service": formatDate(service.next_service),
      ATV: service.atv?.plate_number || "-",
      "ATV Name": service.atv?.name || "-",
      Mechanic: service.mechanic?.name || "-",
      Description: service.description || "-",
      "Inventory Items": (service.inventory_usage?.items || [])
        .map((item) => `${item.inventory_name || item.name || "-"} (${item.quantity || 0} ${item.unit || ""})`)
        .join(", ") || "-",
      "Manual Items": (service.manual_items || [])
        .map((item) => `${item.item_name || "-"} (${item.quantity || 0} ${item.unit || ""})`)
        .join(", ") || "-",
      "All Items": getServiceItemsText(service),
      "Total Inventory Items": service.inventory_usage?.total_items || 0,
      "Total Quantity": service.inventory_usage?.total_quantity || 0,
      "Total Value": service.inventory_usage?.total_value || 0,
    }));

  const fetchServicesForExport = async () => {
    const exportFilters = { ...filters, page: 1, limit: 200 };
    const allServices = [];
    let currentPage = 1;
    let lastPage = 1;

    do {
      const queryParams = { ...exportFilters, page: currentPage };
      Object.keys(queryParams).forEach((key) => {
        if (!queryParams[key]) delete queryParams[key];
      });

      const queryString = buildQueryString(queryParams);
      const response = await getRequest(`/service-reports/services${queryString}`);

      if (!response.success) {
        throw new Error(response.message || "Failed to export service report");
      }

      allServices.push(...(response.data?.services || []));
      lastPage = response.pagination?.last_page || currentPage;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return allServices;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const services = await fetchServicesForExport();
      const rows = buildExportRows(services);
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Service Reports");

      const summaryRows = reportData?.summary
        ? [
            { Metric: "Total Services", Value: reportData.summary.total_services || 0 },
            { Metric: "Total Inventory Items", Value: reportData.summary.total_inventory_items || 0 },
            { Metric: "Total Quantity", Value: reportData.summary.total_quantity || 0 },
            { Metric: "Total Value", Value: reportData.summary.total_value || 0 },
            { Metric: "Start Date", Value: reportData.summary.date_range?.start || filters.start_date || "-" },
            { Metric: "End Date", Value: reportData.summary.date_range?.end || filters.end_date || "-" },
          ]
        : [];
      if (summaryRows.length) {
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), "Summary");
      }

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `service_reports_${filters.start_date || "all"}_${filters.end_date || "all"}.xlsx`);
    } catch (error) {
      console.error("Error exporting service report:", error);
      alert(error.message || "Failed to export service report");
    } finally {
      setExportLoading(false);
    }
  };

  const pagination = reportData?.pagination;
  const currentPage = pagination?.current_page || filters.page || 1;
  const lastPage = pagination?.last_page || 1;
  const totalItems = pagination?.total || 0;

  const paginationPages = () => {
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(lastPage, currentPage + 2);
    for (let page = start; page <= end; page += 1) pages.push(page);
    return pages;
  };

  return (
    <LayoutDashboard>
      <div className="p-3 workshop-page-scope workshop-content-panel">
        <WorkshopTopbar />
        {/* Summary Cards */}
        {reportData?.summary && (
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2 px-3">
                  <div className="h4 mb-0 text-primary">
                    {reportData.summary.total_services}
                  </div>
                  <div className="text-muted small fw-semibold">Total Services</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2 px-3">
                  <div className="h4 mb-0 text-success">
                    {reportData.summary.total_inventory_items}
                  </div>
                  <div className="text-muted small fw-semibold">Inventory Items</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2 px-3">
                  <div className="h4 mb-0 text-info">
                    {reportData.summary.total_quantity}
                  </div>
                  <div className="text-muted small fw-semibold">Total Quantity</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2 px-3">
                  <div className="h4 mb-0 text-warning">
                    {formatPrice(reportData.summary.total_value)}
                  </div>
                  <div className="text-muted small fw-semibold">Total Value</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Filters */}
        <Card className="mb-3">
          <Card.Header className=" py-2 px-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="pe-2">
                <h5 className="mb-0">Service Reports</h5>
                {pagination && (
                  <div className="text-muted small">
                    Page {currentPage}/{lastPage} • {totalItems} services
                  </div>
                )}
                <div className="text-muted small d-none d-md-block">
                  Export Excel mengambil semua data sesuai filter, tidak mengikuti pagination.
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={exportLoading || loading}
                  className="d-inline-flex align-items-center gap-2"
                >
                  {exportLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <IconDownload size={16} />
                  )}
                  {exportLoading ? "Preparing Excel..." : "Export Excel"}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={loadReportData}
                  disabled={loading || exportLoading}
                  className="d-inline-flex align-items-center gap-2"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <IconRefresh size={16} />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="d-inline-flex align-items-center gap-2"
                >
                  {showFilters ? (
                    <>
                      <IconFilterOff size={16} />
                      Hide Filters
                    </>
                  ) : (
                    <>
                      <IconFilter size={16} />
                      Show Filters
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card.Header>

          {showFilters && (
            <Card.Body className="py-2 px-3">
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Start Date</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.start_date}
                      onChange={(e) =>
                        handleFilterChange("start_date", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">End Date</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.end_date}
                      onChange={(e) =>
                        handleFilterChange("end_date", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">ATV</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.atv_id}
                      onChange={(e) =>
                        handleFilterChange("atv_id", e.target.value)
                      }
                    >
                      <option value="">All ATVs</option>
                      {atvs.data?.map((atv) => (
                        <option key={atv.id} value={atv.id}>
                          {atv.plate_number} - {atv.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Warehouse</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.warehouse_id}
                      onChange={(e) =>
                        handleFilterChange("warehouse_id", e.target.value)
                      }
                    >
                      <option value="">All Warehouses</option>
                      {warehouses?.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Category</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.category_id}
                      onChange={(e) =>
                        handleFilterChange("category_id", e.target.value)
                      }
                    >
                      <option value="">All Categories</option>
                      {categories?.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Inventory Item</Form.Label>
                    <AsyncSelect classNamePrefix="react-select" className="react-select-container"
                      loadOptions={loadInventoryOptions}
                      value={
                        filters.inventory_id
                          ? {
                              value: filters.inventory_id,
                              label: "Selected Item",
                            }
                          : null
                      }
                      onChange={(selected) =>
                        handleFilterChange(
                          "inventory_id",
                          selected ? selected.value : ""
                        )
                      }
                      isSearchable
                      placeholder="Search inventory..."
                      isClearable
                      styles={{
                        control: (provided) => ({ ...provided, minHeight: 31, height: 31 }),
                        valueContainer: (provided) => ({ ...provided, paddingTop: 0, paddingBottom: 0 }),
                        indicatorsContainer: (provided) => ({ ...provided, height: 31 }),
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
                    <Form.Control
                      size="sm"
                      type="text"
                      placeholder="Search services..."
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange("search", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Limit</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.limit}
                      onChange={(e) =>
                        handleFilterChange("limit", Number(e.target.value))
                      }
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 border-top mt-2 pt-2">
                <div className="text-muted small d-none d-md-block">
                  Ubah filter untuk memuat ulang data otomatis.
                </div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={resetFilters}
                  disabled={loading || exportLoading}
                >
                  Reset Filters
                </Button>
              </div>
            </Card.Body>
          )}
        </Card>

        {/* Services Table */}
        <Card>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading report data...</p>
              </div>
            ) : reportData?.services?.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Service Date</th>
                        <th>ATV</th>
                        <th>Mechanic</th>
                        <th>Description</th>
                        <th>Inventory Items</th>
                        <th>Manual Items</th>
                        <th>Total Items</th>
                        <th>Total Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.services.map((service) => (
                        <tr key={service.id}>
                          <td>{formatDate(service.service_date)}</td>
                          <td>
                            <div>
                              <strong>{service.atv?.plate_number || "-"}</strong>
                              <br />
                              <small className="text-muted">
                                {service.atv?.name || ""}
                              </small>
                            </div>
                          </td>
                          <td>{service.mechanic?.name || "-"}</td>
                          <td>{service.description || "-"}</td>
                          <td>
                            {service.inventory_usage?.items?.length > 0 ? (
                              <div>
                                {service.inventory_usage.items.map(
                                  (item, index) => (
                                    <div key={index} className="mb-1">
                                      <small>
                                        {item.inventory_name || item.name || "-"} ({item.quantity}{" "}
                                        {item.unit})
                                      </small>
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {service.manual_items?.length > 0 ? (
                              <div>
                                {service.manual_items.map((item, index) => (
                                  <div key={index} className="mb-1">
                                    <Badge bg="secondary" className="me-1 text-white">
                                      Manual
                                    </Badge>
                                    <small>
                                      {item.item_name} ({item.quantity}{" "}
                                      {item.unit})
                                    </small>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>{service.inventory_usage?.total_items || 0}</td>
                          <td>
                            {formatPrice(
                              service.inventory_usage?.total_value || 0
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {pagination && lastPage > 1 && (
                  <div className="d-flex justify-content-between align-items-center border-top mt-3 pt-3 flex-wrap gap-2">
                    <div className="text-muted small">
                      Page {currentPage} of {lastPage} • {totalItems} total data
                    </div>
                    <nav aria-label="Service report pagination">
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => handleFilterChange("page", currentPage - 1)}
                            disabled={currentPage <= 1}
                            aria-label="Previous page"
                          >
                            <IconChevronLeft size={16} />
                          </button>
                        </li>
                        {paginationPages().map((page) => (
                          <li key={page} className={`page-item ${page === currentPage ? "active" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handleFilterChange("page", page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage >= lastPage ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => handleFilterChange("page", currentPage + 1)}
                            disabled={currentPage >= lastPage}
                            aria-label="Next page"
                          >
                            <IconChevronRight size={16} />
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">
                  No services found matching the criteria.
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </LayoutDashboard>
  );
};

export default ServiceReport;
